import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  TextField,
  IconButton,
  Typography,
  Paper,
  Avatar,
  Chip,
  CircularProgress,
  Button,
  Tooltip,
  Zoom,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab,
  Badge,
  Divider,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Alert,
  Snackbar,
  Skeleton,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { styled, keyframes, useTheme } from '@mui/material/styles';
import {
  Psychology,
  Send,
  Close,
  VolumeUp,
  Mic,
  MicOff,
  Minimize,
  Chat,
  Book,
  CheckCircle,
  Error,
  Mood,
  Lightbulb,
  ArrowBack,
  NavigateNext,
  PlayArrow,
  Timer,
  Dashboard,
  School,
  FitnessCenter,
  EmojiEvents,
  Task,
  People,
  TrendingUp,
  AutoAwesome,
  Star,
  ThumbUp,
  Celebration,
  Warning,
  Info,
  Settings,
  MenuBook,
  SportsEsports,
  Add,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { aiAPI } from '../../services/api/aiAPI';
import { gamificationAPI } from '../../services/api/gamificationAPI';
import toast from 'react-hot-toast';
import { useBranding } from '../../contexts/ThemeContext';

// Theme constants
const LIME_GREEN = '#C0EB38';
const AIRA_GRADIENT = 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)';
const CARD_RADIUS = '24px';

// Animations
const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.4); }
  50% { box-shadow: 0 0 40px rgba(59, 130, 246, 0.8); }
`;

// Styled Components
const AiraFab = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(12),
  right: theme.spacing(3),
  background: AIRA_GRADIENT,
  color: 'white',
  width: 72,
  height: 72,
  borderRadius: '24px',
  boxShadow: '0 8px 32px rgba(59, 130, 246, 0.4)',
  animation: `${pulse} 3s infinite, ${float} 4s ease-in-out infinite`,
  '&:hover': {
    background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
    transform: 'scale(1.05)',
    animation: `${glow} 2s infinite`,
  },
  zIndex: theme.zIndex.tooltip + 2,
}));

const MesairaBubble = styled(Paper)(({ theme, isUser }: { theme?: any; isUser: boolean }) => ({
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(1.5),
  maxWidth: '85%',
  alignSelf: isUser ? 'flex-end' : 'flex-start',
  backgroundColor: isUser ? '#3B82F6' : '#FFFFFF',
  color: isUser ? 'white' : '#1A1D1F',
  borderRadius: '20px',
  boxShadow: isUser 
    ? '0 4px 12px rgba(59, 130, 246, 0.3)' 
    : '0 2px 8px rgba(0, 0, 0, 0.06)',
  ...(isUser && {
    borderBottomRightRadius: '6px',
    background: AIRA_GRADIENT,
  }),
  ...(!isUser && {
    borderBottomLeftRadius: '6px',
    border: '1px solid #E2E8F0',
  }),
  animation: 'fadeIn 0.3s ease',
}));

const QuickActionCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  borderRadius: CARD_RADIUS,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    borderColor: LIME_GREEN,
  },
}));

const ContextualTip = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${LIME_GREEN}15 0%, ${LIME_GREEN}05 100%)`,
  border: `1px solid ${LIME_GREEN}30`,
  borderRadius: CARD_RADIUS,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  type?: 'text' | 'suggestion' | 'notification' | 'action';
  actions?: Array<{ label: string; action: () => void }>;
  metadata?: any;
}

interface ContextualSuggestion {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  action: () => void;
  priority: 'high' | 'medium' | 'low';
}

interface QuickAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  action: () => void;
  color: string;
}

interface ProactiveNotification {
  id: string;
  type: 'streak' | 'deadline' | 'achievement' | 'reminder' | 'opportunity';
  title: string;
  message: string;
  icon: React.ReactNode;
  action?: () => void;
  actionLabel?: string;
  priority: 'high' | 'medium' | 'low';
}

function EnhancedAiraAI() {
  const theme = useTheme();
  const { branding } = useBranding();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [proactiveNotifications, setProactiveNotifications] = useState<ProactiveNotification[]>([]);
  const [contextualSuggestions, setContextualSuggestions] = useState<ContextualSuggestion[]>([]);
  const [userStats, setUserStats] = useState<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);

  // Context-aware suggestions based on current page
  const getContextualSuggestions = useCallback((): ContextualSuggestion[] => {
    const suggestions: ContextualSuggestion[] = [];
    const path = location.pathname;

    // Learning page suggestions
    if (path.includes('/learning') || path.includes('/courses')) {
      suggestions.push({
        id: 'learning-help',
        title: 'Need Help?',
        description: 'I can explain concepts, quiz you, or summarize content',
        icon: <School />,
        action: () => handleQuickAction('learning-help'),
        priority: 'high',
      });
      suggestions.push({
        id: 'learning-break',
        title: 'Take a Break',
        description: 'You\'ve been studying for a while. Try a quick wellness exercise?',
        icon: <FitnessCenter />,
        action: () => handleQuickAction('wellness-break'),
        priority: 'medium',
      });
    }

    // Games page suggestions
    if (path.includes('/games') || path.includes('/gamification')) {
      suggestions.push({
        id: 'game-tips',
        title: 'Game Tips',
        description: 'Want tips to improve your score?',
        icon: <EmojiEvents />,
        action: () => handleQuickAction('game-tips'),
        priority: 'high',
      });
      suggestions.push({
        id: 'challenge-recommendation',
        title: 'Recommended Challenge',
        description: 'Based on your level, try this challenge',
        icon: <TrendingUp />,
        action: () => handleQuickAction('challenge-rec'),
        priority: 'medium',
      });
    }

    // Dashboard suggestions
    if (path.includes('/dashboard')) {
      suggestions.push({
        id: 'daily-summary',
        title: 'Daily Summary',
        description: 'Here\'s what\'s happening today',
        icon: <Dashboard />,
        action: () => handleQuickAction('daily-summary'),
        priority: 'high',
      });
      suggestions.push({
        id: 'priority-tasks',
        title: 'Priority Tasks',
        description: 'You have important tasks due',
        icon: <Task />,
        action: () => handleQuickAction('priority-tasks'),
        priority: 'high',
      });
    }

    // Wellness page suggestions
    if (path.includes('/wellness')) {
      suggestions.push({
        id: 'mood-check',
        title: 'Mood Check-in',
        description: 'How are you feeling right now?',
        icon: <Mood />,
        action: () => handleQuickAction('mood-check'),
        priority: 'high',
      });
      suggestions.push({
        id: 'breathing-exercise',
        title: 'Quick Breathing',
        description: '2-minute stress relief exercise',
        icon: <FitnessCenter />,
        action: () => handleQuickAction('breathing'),
        priority: 'medium',
      });
    }

    // Default suggestions
    if (suggestions.length === 0) {
      suggestions.push({
        id: 'general-help',
        title: 'How can I help?',
        description: 'Ask me anything about the platform',
        icon: <Lightbulb />,
        action: () => handleQuickAction('general-help'),
        priority: 'medium',
      });
    }

    return suggestions;
  }, [location.pathname]);

  // Quick actions
  const quickActions: QuickAction[] = [
    {
      id: 'learning',
      label: 'Learning',
      icon: <School />,
      action: () => {
        setActiveTab(0);
        handleQuickAction('learning-help');
      },
      color: '#3B82F6',
    },
    {
      id: 'wellness',
      label: 'Wellness',
      icon: <FitnessCenter />,
      action: () => {
        setActiveTab(1);
        handleQuickAction('mood-check');
      },
      color: LIME_GREEN,
    },
    {
      id: 'games',
      label: 'Games',
      icon: <EmojiEvents />,
      action: () => {
        setActiveTab(2);
        handleQuickAction('game-tips');
      },
      color: '#F59E0B',
    },
    {
      id: 'tasks',
      label: 'Tasks',
      icon: <Task />,
      action: () => {
        handleQuickAction('priority-tasks');
      },
      color: '#8B5CF6',
    },
  ];

  // Load user stats and check for proactive notifications
  useEffect(() => {
    if (open && user?.id) {
      loadUserStats();
      checkProactiveNotifications();
    }
  }, [open, user?.id]);

  // Update contextual suggestions when page changes
  useEffect(() => {
    if (open) {
      setContextualSuggestions(getContextualSuggestions());
    }
  }, [location.pathname, open, getContextualSuggestions]);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;

      recognitionRef.current.onresult = (event: any) => {
        const transcript = Array.from(event.results)
          .map((result: any) => result[0])
          .map((result) => result.transcript)
          .join('');
        
        setInputMessage(transcript);
        
        if (event.results[0].isFinal) {
          handleSendMesaira(transcript);
        }
      };

      recognitionRef.current.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, []);

  const loadUserStats = async () => {
    try {
      const [statsRes] = await Promise.all([
        gamificationAPI.getUserStats().catch(() => ({ data: null })),
      ]);
      
      if (statsRes?.data) {
        setUserStats(statsRes.data);
      }
    } catch (error) {
      console.error('Error loading user stats:', error);
    }
  };

  const checkProactiveNotifications = async () => {
    const notifications: ProactiveNotification[] = [];

    try {
      // Check streak status
      const streaksRes = await gamificationAPI.getUserStats().catch(() => ({ data: null }));
      const userStats: any = streaksRes?.data || {};
      const streak = (userStats as any).streak || 0;
      
      if (streak > 0 && streak % 7 === 0) {
        notifications.push({
          id: 'streak-milestone',
          type: 'streak',
          title: '🔥 Streak Milestone!',
          message: `You have a ${streak}-day streak! Keep it up!`,
          icon: <Celebration />,
          priority: 'high',
        });
      }
      
      // Check for recent achievements
      try {
        const achievementsRes = await gamificationAPI.getUserAchievements().catch(() => ({ data: [] }));
        const recentAchievements = (achievementsRes.data || []).filter((a: any) => {
          const earnedAt = new Date(a.earnedAt);
          const now = new Date();
          const hoursSince = (now.getTime() - earnedAt.getTime()) / (1000 * 60 * 60);
          return hoursSince < 24;
        });

        recentAchievements.forEach((achievement: any) => {
          notifications.push({
            id: `achievement-${achievement.id}`,
            type: 'achievement',
            title: '🏆 Achievement Unlocked!',
            message: `You earned: ${achievement.name}`,
            icon: <Star />,
            priority: 'medium',
          });
        });
      } catch (error) {
        console.log('Could not load achievements');
      }

      setProactiveNotifications(notifications);
    } catch (error) {
      console.error('Error checking notifications:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleOpen = () => {
    setOpen(true);
    setMinimized(false);
    
    // Add welcome message if first time opening
    if (messages.length === 0) {
      const welcomeMessage: Message = {
        id: 'welcome',
        text: `Hi ${user?.firstName || 'there'}! I'm Aira, your AI assistant. I can help you with learning, wellness, games, and more. What would you like to do today?`,
        isUser: false,
        timestamp: new Date(),
        type: 'text',
      };
      setMessages([welcomeMessage]);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleMinimize = () => {
    setMinimized(!minimized);
  };

  const handleSendMesaira = async (messageText?: string) => {
    const text = messageText || inputMessage;
    if (!text.trim()) return;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      text,
      isUser: true,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Get contextual AI response
      const response = await getAIResponse(text);
      
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: response.text,
        isUser: false,
        timestamp: new Date(),
        type: response.type,
        actions: response.actions,
        metadata: response.metadata,
      };
      setMessages(prev => [...prev, aiMessage]);

      // Speak response if voice enabled
      if (voiceEnabled && response.text) {
        speakText(response.text);
      }
    } catch (error) {
      console.error('Error getting AI response:', error);
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: 'I apologize, but I\'m having trouble processing your request right now. Please try again in a moment.',
        isUser: false,
        timestamp: new Date(),
        type: 'text',
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const getAIResponse = async (message: string): Promise<{
    text: string;
    type?: Message['type'];
    actions?: Array<{ label: string; action: () => void }>;
    metadata?: any;
  }> => {
    const lowerMessage = message.toLowerCase();
    const path = location.pathname;

    // Context-aware responses
    if (lowerMessage.includes('help') || lowerMessage.includes('how')) {
      if (path.includes('/learning')) {
        return {
          text: 'I can help you with your learning! I can:\n• Explain difficult concepts\n• Create practice quizzes\n• Summarize course content\n• Recommend next courses\n• Track your progress\n\nWhat would you like help with?',
          type: 'suggestion',
          actions: [
            { label: 'Explain Concept', action: () => handleQuickAction('explain-concept') },
            { label: 'Practice Quiz', action: () => handleQuickAction('practice-quiz') },
            { label: 'Show Progress', action: () => handleQuickAction('show-progress') },
          ],
        };
      }

      if (path.includes('/wellness')) {
        return {
          text: 'I\'m here to support your wellness! I can:\n• Guide you through exercises\n• Track your mood\n• Suggest breaks\n• Monitor your streaks\n• Provide resources\n\nHow are you feeling today?',
          type: 'suggestion',
          actions: [
            { label: 'Mood Check-in', action: () => handleQuickAction('mood-check') },
            { label: 'Quick Exercise', action: () => handleQuickAction('quick-exercise') },
            { label: 'View Goals', action: () => handleQuickAction('view-goals') },
          ],
        };
      }

      if (path.includes('/games')) {
        return {
          text: 'Ready to play? I can:\n• Give you game tips\n• Show leaderboards\n• Recommend challenges\n• Track your high scores\n• Explain game rules\n\nWhat game are you interested in?',
          type: 'suggestion',
          actions: [
            { label: 'Game Tips', action: () => handleQuickAction('game-tips') },
            { label: 'Leaderboard', action: () => handleQuickAction('leaderboard') },
            { label: 'New Challenge', action: () => handleQuickAction('new-challenge') },
          ],
        };
      }

      // General help
      return {
        text: 'I\'m Aira, your AI assistant! I can help you with:\n\n📚 **Learning**: Course help, quizzes, progress tracking\n🏃 **Wellness**: Mood tracking, exercises, goals\n🎮 **Games**: Tips, challenges, leaderboards\n📊 **Analytics**: Your stats and achievements\n\nWhat would you like to explore?',
        type: 'suggestion',
        actions: [
          { label: 'Learning', action: () => navigate('/app/learning') },
          { label: 'Wellness', action: () => navigate('/app/wellness') },
          { label: 'Games', action: () => navigate('/app/games') },
          { label: 'My Stats', action: () => handleQuickAction('my-stats') },
        ],
      };
    }

    // Stats query
    if (lowerMessage.includes('stat') || lowerMessage.includes('progress') || lowerMessage.includes('score')) {
      if (userStats) {
        return {
          text: `Here are your current stats:\n\n📊 **Level**: ${userStats.level || 1}\n⭐ **Points**: ${userStats.totalPoints || 0}\n🔥 **Streak**: ${userStats.streak || 0} days\n🏆 **Achievements**: ${userStats.achievements || 0}\n📈 **Rank**: #${userStats.rank || 'N/A'}\n\nKeep up the great work!`,
          type: 'notification',
          metadata: userStats,
        };
      }
      return {
        text: 'Let me get your latest stats...',
        type: 'text',
      };
    }

    // Motivational responses
    if (lowerMessage.includes('tired') || lowerMessage.includes('stressed') || lowerMessage.includes('overwhelmed')) {
      return {
        text: 'I hear you, and it\'s okay to feel this way. Let\'s take a moment for yourself. Would you like to:\n\n• Try a 2-minute breathing exercise?\n• Take a quick wellness break?\n• Talk about what\'s bothering you?\n• See some motivational quotes?\n\nRemember, small steps lead to big changes! 💚',
        type: 'suggestion',
        actions: [
          { label: 'Breathing Exercise', action: () => handleQuickAction('breathing') },
          { label: 'Take a Break', action: () => handleQuickAction('wellness-break') },
          { label: 'Motivation', action: () => handleQuickAction('motivation') },
        ],
      };
    }

    // Default response
    return {
      text: 'Thanks for your message! I\'m here to help you make the most of MoreMe. You can ask me about:\n\n• Learning & courses\n• Wellness & mental health\n• Games & challenges\n• Your progress & stats\n• Platform features\n\nWhat would you like to know?',
      type: 'text',
    };
  };

  const speakText = (text: string) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      speechSynthesis.speak(utterance);
    }
  };

  const handleStartListening = () => {
    if (recognitionRef.current) {
      setIsListening(true);
      recognitionRef.current.start();
    } else {
      toast.error('Speech recognition is not supported in your browser');
    }
  };

  const handleQuickAction = (actionId: string) => {
    switch (actionId) {
      case 'learning-help':
        setActiveTab(0);
        handleSendMesaira('I need help with learning');
        break;
      case 'wellness-break':
        setActiveTab(1);
        handleSendMesaira('I need a wellness break');
        break;
      case 'game-tips':
        setActiveTab(2);
        handleSendMesaira('Give me game tips');
        break;
      case 'challenge-rec':
        navigate('/app/gamification/challenges');
        break;
      case 'daily-summary':
        handleSendMesaira('Show me my daily summary');
        break;
      case 'priority-tasks':
        navigate('/app/tasks');
        break;
      case 'mood-check':
        setActiveTab(1);
        handleSendMesaira('Let\'s do a mood check-in');
        break;
      case 'breathing':
        setActiveTab(1);
        handleSendMesaira('Guide me through a breathing exercise');
        break;
      case 'general-help':
        handleSendMesaira('Help');
        break;
      case 'explain-concept':
        handleSendMesaira('Can you explain this concept?');
        break;
      case 'practice-quiz':
        handleSendMesaira('Create a practice quiz for me');
        break;
      case 'show-progress':
        handleSendMesaira('Show my learning progress');
        break;
      case 'quick-exercise':
        setActiveTab(1);
        handleSendMesaira('Show me a quick exercise');
        break;
      case 'view-goals':
        handleSendMesaira('Show my wellness goals');
        break;
      case 'leaderboard':
        navigate('/app/gamification/leaderboard');
        break;
      case 'new-challenge':
        navigate('/app/gamification/challenges');
        break;
      case 'my-stats':
        handleSendMesaira('Show my stats');
        break;
      case 'motivation':
        handleSendMesaira('I need some motivation');
        break;
      case 'maintain-login-streak':
        // Record login activity - simplified for current API
        toast.success('Login streak maintained! 🔥');
        break;
      default:
        console.log('Unknown action:', actionId);
    }
  };

  const dismissNotification = (notificationId: string) => {
    setProactiveNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  return (
    <>
      <Zoom in={!open} style={{ transitionDelay: '200ms' }}>
        <AiraFab onClick={handleOpen} aria-label="Open Aira AI Assistant">
          <Psychology sx={{ fontSize: 32 }} />
        </AiraFab>
      </Zoom>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: CARD_RADIUS,
            height: '80vh',
            maxHeight: '800px',
          },
        }}
      >
        <DialogTitle sx={{ p: 2, pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ bgcolor: AIRA_GRADIENT, width: 48, height: 48 }}>
                <Psychology />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Aira AI Assistant
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {userStats ? `Level ${userStats.level || 1} • ${userStats.totalPoints || 0} points` : 'Your personal assistant'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={voiceEnabled ? 'Disable Voice' : 'Enable Voice'}>
                <IconButton onClick={() => setVoiceEnabled(!voiceEnabled)} color={voiceEnabled ? 'primary' : 'default'}>
                  {voiceEnabled ? <VolumeUp /> : <VolumeUp sx={{ opacity: 0.5 }} />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Minimize">
                <IconButton onClick={handleMinimize}>
                  <Minimize />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton onClick={handleClose}>
                  <Close />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            sx={{ mt: 2 }}
            variant="scrollable"
          >
            <Tab icon={<Chat />} iconPosition="start" label="Chat" />
            <Tab icon={<Mood />} iconPosition="start" label="Wellness" />
            <Tab icon={<EmojiEvents />} iconPosition="start" label="Games" />
            <Tab icon={<Dashboard />} iconPosition="start" label="Stats" />
          </Tabs>
        </DialogTitle>

        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {minimized ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Aira is here when you need support
              </Typography>
              <Button
                variant="contained"
                onClick={() => setMinimized(false)}
                sx={{ mt: 2 }}
              >
                Open Full Interface
              </Button>
            </Box>
          ) : (
            <>
              {/* Proactive Notifications */}
              {proactiveNotifications.length > 0 && activeTab === 0 && (
                <Box sx={{ p: 2 }}>
                  {proactiveNotifications.map(notification => (
                    <Alert
                      key={notification.id}
                      severity={notification.priority === 'high' ? 'warning' : 'info'}
                      icon={notification.icon}
                      onClose={() => dismissNotification(notification.id)}
                      sx={{ mb: 1, borderRadius: CARD_RADIUS }}
                      action={
                        notification.action && (
                          <Button
                            color="inherit"
                            size="small"
                            onClick={notification.action}
                          >
                            {notification.actionLabel || 'Action'}
                          </Button>
                        )
                      }
                    >
                      <Typography variant="subtitle2" fontWeight={600}>
                        {notification.title}
                      </Typography>
                      <Typography variant="body2">{notification.message}</Typography>
                    </Alert>
                  ))}
                </Box>
              )}

              {/* Contextual Suggestions */}
              {contextualSuggestions.length > 0 && activeTab === 0 && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Suggestions for this page:
                  </Typography>
                  <Grid container spacing={1.5}>
                    {contextualSuggestions.map(suggestion => (
                      <Grid item xs={12} sm={6} key={suggestion.id}>
                        <QuickActionCard onClick={suggestion.action}>
                          <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                              <Avatar sx={{ bgcolor: `${suggestion.priority === 'high' ? '#EF4444' : '#3B82F6'}20`, color: suggestion.priority === 'high' ? '#EF4444' : '#3B82F6' }}>
                                {suggestion.icon}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" fontWeight={600}>
                                  {suggestion.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {suggestion.description}
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </QuickActionCard>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Chat Messages */}
              {activeTab === 0 && (
                <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column' }}>
                  {messages.map(message => (
                    <MesairaBubble
                      key={message.id}
                      isUser={message.isUser}
                      elevation={0}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {message.text}
                      </Typography>
                      {message.actions && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                          {message.actions.map((action, idx) => (
                            <Chip
                              key={idx}
                              label={action.label}
                              size="small"
                              onClick={action.action}
                              sx={{
                                bgcolor: message.isUser ? 'rgba(255,255,255,0.2)' : '#3B82F6',
                                color: 'white',
                                '&:hover': {
                                  bgcolor: message.isUser ? 'rgba(255,255,255,0.3)' : '#2563EB',
                                },
                              }}
                            />
                          ))}
                        </Box>
                      )}
                      <Typography variant="caption" sx={{ mt: 0.5, opacity: 0.7 }}>
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </MesairaBubble>
                  ))}
                  {isLoading && (
                    <MesairaBubble isUser={false} elevation={0}>
                      <CircularProgress size={20} />
                    </MesairaBubble>
                  )}
                  <div ref={messagesEndRef} />
                </Box>
              )}

              {/* Quick Actions */}
              {showQuickActions && activeTab === 0 && (
                <Box sx={{ p: 2, borderTop: '1px solid #E2E8F0' }}>
                  <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                    Quick Actions:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {quickActions.map(action => (
                      <Chip
                        key={action.id}
                        label={action.label}
                        onClick={action.action}
                        sx={{
                          bgcolor: `${action.color}15`,
                          color: action.color,
                          '&:hover': {
                            bgcolor: `${action.color}25`,
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Input Area */}
              {activeTab === 0 && (
                <Box sx={{ p: 2, borderTop: '1px solid #E2E8F0' }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                    <TextField
                      fullWidth
                      placeholder="Ask Aira anything..."
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMesaira()}
                      multiline
                      maxRows={4}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '20px',
                        },
                      }}
                    />
                    <Tooltip title="Voice Input">
                      <IconButton
                        onClick={handleStartListening}
                        color={isListening ? 'error' : 'default'}
                        sx={{
                          animation: isListening ? `${pulse} 1s infinite` : 'none',
                        }}
                      >
                        {isListening ? <MicOff /> : <Mic />}
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      onClick={() => handleSendMesaira()}
                      disabled={!inputMessage.trim() || isLoading}
                      sx={{
                        bgcolor: LIME_GREEN,
                        color: '#1A1D1F',
                        '&:hover': {
                          bgcolor: '#A8D42F',
                        },
                        '&.Mui-disabled': {
                          bgcolor: 'rgba(0, 0, 0, 0.12)',
                          color: 'rgba(0, 0, 0, 0.26)',
                        },
                      }}
                    >
                      <Send />
                    </IconButton>
                  </Box>
                </Box>
              )}

              {/* Wellness Tab */}
              {activeTab === 1 && (
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Wellness Check-in
                  </Typography>
                  <QuickActionCard onClick={() => handleQuickAction('mood-check')}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: `${LIME_GREEN}20`, color: LIME_GREEN }}>
                          <Mood />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            How are you feeling?
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Track your mood and get personalized recommendations
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </QuickActionCard>
                </Box>
              )}

              {/* Games Tab */}
              {activeTab === 2 && (
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Gaming Hub
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <QuickActionCard onClick={() => handleQuickAction('game-tips')}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: '#F59E0B20', color: '#F59E0B' }}>
                              <Lightbulb />
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle1" fontWeight={600}>
                                Game Tips
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Improve your scores
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </QuickActionCard>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <QuickActionCard onClick={() => handleQuickAction('leaderboard')}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: '#3B82F620', color: '#3B82F6' }}>
                              <TrendingUp />
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle1" fontWeight={600}>
                                Leaderboard
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                See your rank
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </QuickActionCard>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Stats Tab */}
              {activeTab === 3 && (
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Your Stats
                  </Typography>
                  {userStats ? (
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Card sx={{ textAlign: 'center', borderRadius: CARD_RADIUS }}>
                          <CardContent>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: LIME_GREEN }}>
                              {userStats.level || 1}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Level
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Card sx={{ textAlign: 'center', borderRadius: CARD_RADIUS }}>
                          <CardContent>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#3B82F6' }}>
                              {userStats.totalPoints?.toLocaleString() || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Points
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Card sx={{ textAlign: 'center', borderRadius: CARD_RADIUS }}>
                          <CardContent>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#EF4444' }}>
                              {userStats.streak || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Day Streak
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Card sx={{ textAlign: 'center', borderRadius: CARD_RADIUS }}>
                          <CardContent>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#F59E0B' }}>
                              {userStats.achievements || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Achievements
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress />
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default EnhancedAiraAI;
